import React from 'react';

/** Zeilen-Deltas (Firefox) sind keine Pixel — grob auf Zeilenhöhe umrechnen. */
const LINE_HEIGHT_PX = 16;

export type WheelScrollInput = {
  deltaX: number;
  deltaY: number;
  /** WheelEvent.deltaMode: 0 = Pixel, 1 = Zeilen, 2 = Seiten. */
  deltaMode: number;
  shiftKey: boolean;
  ctrlKey: boolean;
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
};

/**
 * Neuer `scrollLeft` für ein Radereignis, oder `null` wenn das Ereignis dem
 * Browser überlassen werden soll (vertikal scrollen, zoomen, Seite weiterscrollen).
 *
 * Nur Shift+Rad wird übernommen. Ein reines `deltaY` ohne Shift ist auch die
 * Zwei-Finger-Geste am Touchpad — die muss weiter nach unten scrollen.
 */
export function horizontalWheelScrollTarget(input: WheelScrollInput): number | null {
  if (!input.shiftKey) return null;
  // Strg+Rad ist Zoom, und ein waagerechtes Delta liefert das Touchpad schon selbst.
  if (input.ctrlKey) return null;
  if (Math.abs(input.deltaX) > Math.abs(input.deltaY)) return null;

  const maxScrollLeft = input.scrollWidth - input.clientWidth;
  if (maxScrollLeft <= 1) return null;

  const pixelsPerUnit =
    input.deltaMode === 1 ? LINE_HEIGHT_PX : input.deltaMode === 2 ? input.clientWidth : 1;
  const delta = input.deltaY * pixelsPerUnit;
  if (delta === 0) return null;

  const next = Math.max(0, Math.min(maxScrollLeft, input.scrollLeft + delta));
  // Am Rand nicht abfangen, damit die Seite dahinter weiterscrollen kann.
  if (next === input.scrollLeft) return null;
  return next;
}

function useWheelHandler(node: HTMLElement | null): void {
  React.useEffect(() => {
    if (!node) return;
    const handleWheel = (event: WheelEvent) => {
      if (event.defaultPrevented) return;
      const target = horizontalWheelScrollTarget({
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaMode: event.deltaMode,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        scrollLeft: node.scrollLeft,
        scrollWidth: node.scrollWidth,
        clientWidth: node.clientWidth,
      });
      if (target === null) return;
      event.preventDefault();
      node.scrollLeft = target;
    };
    // Nicht passiv, sonst darf preventDefault das Standardscrollen nicht ersetzen.
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [node]);
}

/**
 * Waagerechtes Scrollen per Shift+Mausrad in Containern, die breiter als der
 * Viewport sind. Gibt eine Ref-Callback zurück; ein vorhandenes Ref-Objekt kann
 * über `forwardTo` mitbefüllt werden.
 */
export function useHorizontalWheelScroll<T extends HTMLElement>(
  forwardTo?: React.RefObject<T | null>,
): (node: T | null) => void {
  const [node, setNode] = React.useState<T | null>(null);

  const setRef = React.useCallback(
    (next: T | null) => {
      if (forwardTo) forwardTo.current = next;
      setNode(next);
    },
    [forwardTo],
  );

  useWheelHandler(node);

  return setRef;
}

/**
 * Wie `useHorizontalWheelScroll`, gibt den Knoten zusätzlich heraus — die
 * angedockte Scrollleiste braucht dessen Maße und Scrollposition.
 */
export function useHorizontalScrollTarget<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  node: T | null;
} {
  const [node, setNode] = React.useState<T | null>(null);
  useWheelHandler(node);
  return { ref: setNode, node };
}
